import React from 'react'

import { CustomError } from 'shared/errors/base'

import { componentCache } from './componentCache'
import { SuspensePageChunkLoadError, SuspensePageMissingComponentError } from './errors'
import { SuspensePageProps } from './interfaces'
import { SuspensePageLoading } from './SuspensePageLoading'

const RETRY_DELAY = 500

function wait(timeout: number = 0) {
  return new Promise((resolve) => setTimeout(resolve, timeout))
}

export function SuspensePageComponent({ alias, importer, loadingFallback }: SuspensePageProps) {
  const LazyComponent = React.useMemo(
    () =>
      React.lazy(() => {
        const route = alias || 'unknown'

        return importer()
          .catch(async () => {
            await wait(RETRY_DELAY)
            return importer()
          })
          .then((module) => {
            let Component = module.Component

            if (!Component && 'default' in module) {
              Component = (module as { default: React.ComponentType<TSFixMe> }).default
            }

            if (!Component) {
              throw new SuspensePageMissingComponentError()
            }

            componentCache.set(importer, Component)

            return { default: Component }
          })
          .catch((error: unknown) => {
            throw error instanceof CustomError
              ? error
              : new SuspensePageChunkLoadError(error, route)
          })
      }),
    [alias, importer],
  )

  return (
    <React.Suspense fallback={loadingFallback ?? <SuspensePageLoading />}>
      <LazyComponent />
    </React.Suspense>
  )
}
